import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, MessageCircle, BellRing, ClipboardList, Settings2, GripHorizontal, MapPin } from "lucide-react";
import { aiApi, roleAI } from "./aiApi";
import MickeyAIIcon, { MICKEY_AI_STYLES } from "./MickeyAIIcon";
import { useMickeyMood, setMickeyMood } from "./mickeyMood";
import MickeySpeech, { greetingFor } from "./MickeySpeech";
import AIChat from "./AIChat";
import AIInbox from "./AIInbox";
import AITasks from "./AITasks";
import AIAutomation from "./AIAutomation";

const SIZE = 68;
const POS_KEY = "ai_bubble_pos_v1";
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const defaultPos = () => ({ x: window.innerWidth - SIZE - 18, y: window.innerHeight - SIZE - (window.innerWidth < 1024 ? 110 : 28) });
const fit = (p) => ({ x: clamp(p.x, 8, window.innerWidth - SIZE - 8), y: clamp(p.y, 8, window.innerHeight - SIZE - 8) });

export default function AIBubble({ user }) {
  const ai = roleAI(user.role);
  const location = useLocation();
  const navigate = useNavigate();
  const [pos, setPos] = useState(() => {
    try { return fit(JSON.parse(localStorage.getItem(POS_KEY)) || defaultPos()); } catch { return fit(defaultPos()); }
  });
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("chat");
  const [summary, setSummary] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const drag = useRef(null);
  const rawMood = useMickeyMood();
  const [speech, setSpeech] = useState(null); // {kind, title, text, Icon, tone}
  const [sadDismissed, setSadDismissed] = useState(false);
  const waFailed = user.role === "superadmin" ? (summary?.wa_failed || 0) : 0;
  // Mickey sedih bila laporan WhatsApp gagal (jika tiada ekspresi lain sedang berlaku)
  const mood = rawMood === "idle" && waFailed > 0 ? "sad" : rawMood;

  const refresh = useCallback(() => aiApi.summary().then(setSummary).catch(() => {}), []);
  useEffect(() => { refresh(); const t = setInterval(refresh, 60000); return () => clearInterval(t); }, [refresh, user.id]);
  useEffect(() => {
    const onRefresh = () => refresh();
    window.addEventListener("ai:refresh-summary", onRefresh);
    return () => window.removeEventListener("ai:refresh-summary", onRefresh);
  }, [refresh]);
  // Sapaan sekali setiap sesi: Mickey melambai + ucapan ikut waktu
  useEffect(() => {
    const key = `mk_greet_${user.id}`;
    if (sessionStorage.getItem(key)) return;
    const t1 = setTimeout(() => {
      sessionStorage.setItem(key, "1");
      const g = greetingFor(user.name, user.role);
      setSpeech({ kind: "greet", ...g });
      setMickeyMood("wave", 6000);
    }, 1200);
    const t2 = setTimeout(() => setSpeech((s) => (s?.kind === "greet" ? null : s)), 8500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [user.id, user.name, user.role]);
  useEffect(() => { if (waFailed === 0) setSadDismissed(false); }, [waFailed]);
  useEffect(() => {
    const onResize = () => { setIsMobile(window.innerWidth < 640); setPos((p) => fit(p)); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get("ai")) {
      setOpen(true);
      setTab(q.get("ai") === "inbox" ? "inbox" : q.get("ai") === "tasks" ? "tasks" : "chat");
      q.delete("ai");
      navigate({ pathname: location.pathname, search: q.toString() }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  const onDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < 6) return;
    d.moved = true;
    setDragging(true);
    setPos(fit({ x: d.ox + dx, y: d.oy + dy }));
  };
  const onUp = () => {
    const d = drag.current;
    drag.current = null;
    setDragging(false);
    if (!d) return;
    if (d.moved) {
      setPos((p) => { localStorage.setItem(POS_KEY, JSON.stringify(p)); return p; });
    } else {
      setOpen((o) => !o);
      refresh();
    }
  };

  // Superadmin boleh sembunyikan gelembung Mimi Junior ikut peranan (AI > Tetapan > Paparan Mimi Junior)
  if (user.role !== "superadmin" && (!summary || summary.bubble_visible === false)) return null;

  const badge = (summary?.unread || 0) + (summary?.pending_tasks || 0);
  const tabs = [
    { k: "chat", label: "Chat", icon: MessageCircle },
    { k: "inbox", label: "Peringatan", icon: BellRing, n: summary?.unread },
    { k: "tasks", label: user.role === "ibu_bapa" ? "Makluman Am" : "Tugasan", icon: ClipboardList, n: summary?.pending_tasks },
    ...(user.role === "superadmin" ? [{ k: "auto", label: "Tetapan", icon: Settings2 }] : []),
  ];

  // Panel desktop: diletak bersebelahan gelembung (kiri/kanan ikut kedudukan)
  const panelW = 420;
  const panelH = Math.min(680, window.innerHeight - 32);
  const leftSide = pos.x + SIZE / 2 < window.innerWidth / 2;
  const panelStyle = isMobile ? {} : {
    width: panelW, height: panelH,
    left: clamp(leftSide ? pos.x + SIZE + 14 : pos.x - panelW - 14, 12, window.innerWidth - panelW - 12),
    top: clamp(pos.y + SIZE / 2 - panelH / 2, 16, window.innerHeight - panelH - 16),
  };

  return (
    <>
      <style>{`@keyframes aiFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
        @keyframes aiPop{from{opacity:0;transform:scale(.92) translateY(10px)}to{opacity:1;transform:scale(1) translateY(0)}}
        .ai-float{animation:aiFloat 3.2s ease-in-out infinite}.ai-pop{animation:aiPop .22s ease-out}${MICKEY_AI_STYLES}`}</style>

      {open && (
        <div className={`fixed z-[9998] ai-pop flex flex-col bg-gradient-to-b from-white to-slate-50 overflow-hidden ${isMobile ? "inset-0" : "rounded-3xl shadow-2xl border border-slate-200"}`}
          style={panelStyle} data-testid="ai-panel">
          <div className={`relative bg-gradient-to-r ${ai.grad} text-white px-4 pt-3.5 pb-3 shadow-md`} style={isMobile ? { paddingTop: "max(14px, env(safe-area-inset-top))" } : {}}>
            <div className="flex items-center gap-3">
              <div className="mk-hover h-12 w-12 shrink-0 rounded-2xl bg-white/20 ring-1 ring-white/30 backdrop-blur-md grid place-items-center shadow-inner">
                <MickeyAIIcon role={user.role} mood={mood} className="h-10 w-10 drop-shadow" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-lg font-black tracking-tight leading-none drop-shadow-sm" data-testid="ai-panel-title">{ai.name}</div>
                <div className="mt-1 text-[10.5px] font-semibold uppercase tracking-wider text-white/85 truncate" data-testid="ai-panel-subtitle">{ai.roleLabel}</div>
              </div>
              <button onClick={() => setOpen(false)} className="h-9 w-9 shrink-0 rounded-xl bg-white/15 hover:bg-white/25 grid place-items-center transition-colors" aria-label="Tutup" data-testid="ai-panel-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-2.5 flex items-center gap-2 min-w-0">
              <div className="shrink-0 max-w-[60%] truncate text-sm font-bold capitalize" data-testid="ai-panel-greeting">
                {user.role === "ibu_bapa" ? <>Hai, {(user.name || "").toLowerCase()}! 👋</> : (user.name || "").toLowerCase()}
              </div>
              {user.branch && (
                <span className="min-w-0 inline-flex items-center gap-1 rounded-full bg-white/20 ring-1 ring-white/25 px-2 py-0.5 text-[10.5px] font-semibold text-white/95" data-testid="ai-panel-branch">
                  <MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{user.branch}</span>
                </span>
              )}
            </div>
            <div className="flex gap-1 mt-3 p-1 rounded-2xl bg-black/10 ring-1 ring-white/15 backdrop-blur-md">
              {tabs.map((t) => {
                const Icon = t.icon;
                return (
                  <button key={t.k} onClick={() => { setTab(t.k); refresh(); }} data-testid={`ai-tab-${t.k}`}
                    className={`relative flex-1 min-w-0 flex items-center justify-center gap-1 rounded-xl py-1.5 text-[12px] font-bold transition-[background-color,color,box-shadow] duration-200 ${tab === t.k ? "bg-white text-slate-800 shadow-sm" : "text-white/90 hover:bg-white/15"}`}>
                    <Icon className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{t.label}</span>
                    {t.n > 0 && <span className="absolute -top-1.5 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-black grid place-items-center ring-2 ring-white shadow-sm">{t.n > 99 ? "99+" : t.n}</span>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex-1 min-h-0">
            {tab === "chat" && <AIChat user={user} summary={summary} />}
            {tab === "inbox" && <AIInbox onRead={refresh} user={user} />}
            {tab === "tasks" && <AITasks user={user} onChange={refresh} />}
            {tab === "auto" && <AIAutomation />}
          </div>
        </div>
      )}

      {!open && !dragging && speech && (
        <MickeySpeech pos={pos} size={SIZE} {...speech}
          onClick={() => { setSpeech(null); setTab("chat"); setOpen(true); }} onClose={() => setSpeech(null)} />
      )}
      {!open && !dragging && !speech && waFailed > 0 && !sadDismissed && (
        <MickeySpeech pos={pos} size={SIZE} kind="sad" title="Alamak, saya perlukan bantuan!"
          text={`${waFailed} laporan WhatsApp gagal dihantar. Tekan untuk semak sambungan Wabot.`}
          onClick={() => { setTab("auto"); setOpen(true); }} onClose={() => setSadDismissed(true)} />
      )}

      {!(open && isMobile) && (
        <div
          role="button" tabIndex={0} aria-label={`Buka ${ai.name}`} data-testid="ai-bubble" data-mood={mood}
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen((o) => !o)}
          className={`fixed z-[9999] select-none touch-none ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
          style={{ left: pos.x, top: pos.y, width: SIZE, height: SIZE }}
        >
          <div className={`mk-hover relative h-full w-full ${dragging || open ? "" : "ai-float"}`}>
            {badge > 0 && !open && (
              <MickeyAIIcon role={user.role} variant="silhouette" className="absolute inset-0 h-full w-full opacity-50 animate-ping" />
            )}
            <div className={`relative h-full w-full transition-transform duration-200 drop-shadow-[0_10px_14px_rgba(236,72,153,0.45)] ${dragging ? "scale-110" : "hover:scale-110 active:scale-95"}`}
              data-testid="ai-bubble-mickey">
              <MickeyAIIcon role={user.role} variant={open ? "close" : "face"} mood={mood} className="h-full w-full" />
              <GripHorizontal className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 h-3 w-3 text-slate-400/80" />
            </div>
            {badge > 0 && !open && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1 rounded-full bg-rose-500 text-white text-[11px] font-extrabold grid place-items-center ring-2 ring-white" data-testid="ai-bubble-badge">{badge > 99 ? "99+" : badge}</span>
            )}
          </div>
        </div>
      )}
    </>
  );
}
